<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-gray-900">إدارة جداول المعلمين</h2>
        <div class="flex gap-2">
          <button 
            v-if="selectedTeacherId"
            @click="toggleViewMode"
            class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
            </svg>
            {{ viewMode === 'table' ? 'عرض مجمّع' : 'عرض جدول' }}
          </button>
          <button 
            @click="openCreateModal"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            إضافة جدول جديد (سريع)
          </button>
        </div>
      </div>

      <!-- Teacher Selector & Filters -->
      <div class="mb-6 bg-white p-4 rounded-lg shadow">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">اختر المعلم</label>
            <select 
              v-model="selectedTeacherId" 
              @change="loadSchedules"
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">اختر المعلم</option>
              <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                {{ teacher.fullName }}
              </option>
            </select>
          </div>
          <div v-if="selectedTeacherId && schedules.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-2">فلترة حسب الحالة</label>
            <select 
              v-model="filterActive" 
              @change="applyFilter"
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">الكل</option>
              <option value="true">نشط فقط</option>
              <option value="false">غير نشط فقط</option>
            </select>
          </div>
        </div>
        <div v-if="selectedTeacherId && schedules.length > 0" class="mt-4 text-sm text-gray-600">
          <span class="font-medium">إجمالي الجداول:</span> {{ filteredSchedules.length }}
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-2 text-gray-600">جاري تحميل الجداول...</p>
      </div>

      <!-- View Mode: Table -->
      <div v-if="viewMode === 'table' && selectedTeacherId && filteredSchedules.length > 0" class="bg-white shadow-lg rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الصلاة</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت البداية</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت النهاية</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">يوم الأسبوع</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عدد الجلسات</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">مدة الجلسة</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المدة الإجمالية</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الحالة</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الإجراءات</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="schedule in filteredSchedules" :key="schedule.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ getPrayerNameArabic(schedule.prayerName) || 'N/A' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ formatTime(schedule.sessionStartTime) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ formatTime(schedule.sessionEndTime) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.dayName || 'كل الأيام' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.sessionCount }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.sessionDurationMinutes }} دقيقة
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.totalDurationMinutes }} دقيقة
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span 
                    :class="schedule.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ schedule.isActive ? 'نشط' : 'غير نشط' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex gap-2">
                    <button 
                      @click="openEditModal(schedule)"
                      class="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded"
                    >
                      تعديل
                    </button>
                    <button 
                      @click="deleteSchedule(schedule)"
                      class="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
                    >
                      حذف
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- View Mode: Grouped by Schedule Group ID -->
      <div v-if="viewMode === 'grouped' && selectedTeacherId && orderedSchedulesByGroup" class="space-y-6">
        <template v-for="(groupSchedules, groupId) in orderedSchedulesByGroup" :key="groupId">
          <div v-if="groupSchedules && groupSchedules.length > 0" class="bg-white shadow-lg rounded-lg overflow-hidden">
          <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">
              <span v-if="!groupId.startsWith('no-group')">الجدول رقم {{ groupId }}</span>
              <span v-else>جداول غير مجمعة</span>
              <span class="text-sm font-normal text-gray-600">
                ({{ groupSchedules.length }} {{ groupSchedules.length === 1 ? 'يوم' : 'أيام' }})
              </span>
              <span v-if="groupSchedules.length > 0" class="text-xs text-gray-500 block mt-1">
                الأيام: {{ groupSchedules.map(s => s.dayName || 'كل الأيام').join('، ') }}
              </span>
            </h3>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الصلاة</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت البداية</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت النهاية</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">يوم الأسبوع</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عدد الجلسات</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">مدة الجلسة</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المدة الإجمالية</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الحالة</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="schedule in groupSchedules" :key="schedule.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {{ getPrayerNameArabic(schedule.prayerName) || 'N/A' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ formatTime(schedule.sessionStartTime) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ formatTime(schedule.sessionEndTime) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.dayName || 'كل الأيام' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.sessionCount }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.sessionDurationMinutes }} دقيقة
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.totalDurationMinutes }} دقيقة
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span 
                      :class="schedule.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    >
                      {{ schedule.isActive ? 'نشط' : 'غير نشط' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex gap-2">
                      <button 
                        @click="openEditModal(schedule)"
                        class="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded"
                      >
                        تعديل
                      </button>
                      <button 
                        @click="deleteSchedule(schedule)"
                        class="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </template>
      </div>

      <!-- Empty State (Table View) -->
      <div v-else-if="viewMode === 'table' && selectedTeacherId && !loading && filteredSchedules.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
        <div class="mx-auto h-12 w-12 text-gray-400">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
        </div>
        <h3 class="mt-2 text-sm font-medium text-gray-900">لا توجد جداول</h3>
        <p class="mt-1 text-sm text-gray-500" v-if="filterActive !== 'all'">لا توجد جداول تطابق الفلتر المحدد</p>
        <p class="mt-1 text-sm text-gray-500" v-else>ابدأ بإضافة جدول جديد للمعلم</p>
      </div>

      <!-- Empty State (Grouped View) -->
      <div v-else-if="viewMode === 'grouped' && selectedTeacherId && !loading && (!orderedSchedulesByGroup || Object.keys(orderedSchedulesByGroup).length === 0)" class="text-center py-12 bg-white rounded-lg shadow">
        <div class="mx-auto h-12 w-12 text-gray-400">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
        </div>
        <h3 class="mt-2 text-sm font-medium text-gray-900">لا توجد جداول</h3>
        <p class="mt-1 text-sm text-gray-500">ابدأ بإضافة جدول جديد للمعلم</p>
      </div>

      <!-- Create/Edit Modal -->
      <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click.self="closeModal">
        <div class="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">
          <div class="mt-3">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium text-gray-900">
                {{ editingSchedule ? 'تعديل الجدول' : 'إضافة جدول جديد' }}
              </h3>
              <div class="flex gap-2">
                <button
                  @click="useCompleteForm = true"
                  :class="useCompleteForm ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
                  class="px-3 py-1 rounded text-sm"
                >
                  إنشاء سريع
                </button>
                <button
                  @click="useCompleteForm = false"
                  :class="!useCompleteForm ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
                  class="px-3 py-1 rounded text-sm"
                >
                  إنشاء تقليدي
                </button>
              </div>
            </div>
            
            <!-- Complete Form (One Step) -->
            <form v-if="useCompleteForm" @submit.prevent="submitCompleteForm" class="space-y-4">
              <!-- Teacher Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">المعلم</label>
                <select 
                  v-model="completeForm.teacher_id" 
                  @change="onTeacherChange"
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر المعلم</option>
                  <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                    {{ teacher.fullName }}
                  </option>
                </select>
              </div>

              <!-- Region Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">المنطقة</label>
                <select 
                  v-model="completeForm.region_id" 
                  @change="onRegionChange"
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر المنطقة</option>
                  <option v-for="region in regions" :key="region.id" :value="region.id">
                    {{ region.regionNameAr || region.region_name_ar || region.regionNameEn || region.region_name_en }}
                  </option>
                </select>
              </div>

              <!-- Prayer Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الصلاة</label>
                <select 
                  v-model="completeForm.prayer_name" 
                  @change="onPrayerChange"
                  required
                  :disabled="!completeForm.region_id || loadingPrayerTime"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر الصلاة</option>
                  <option value="FAJR">الفجر</option>
                  <option value="DHUHR">الظهر</option>
                  <option value="ASR">العصر</option>
                  <option value="MAGHRIB">المغرب</option>
                  <option value="ISHA">العشاء</option>
                </select>
                <div v-if="loadingPrayerTime" class="text-xs text-blue-600 mt-1">
                  جاري جلب وقت الصلاة...
                </div>
                <div v-if="completeForm.prayer_time && !loadingPrayerTime" class="text-xs text-green-600 mt-1">
                  ✓ وقت الصلاة: {{ formatTime(completeForm.prayer_time) }}
                </div>
              </div>

              <!-- First Session Start Minutes After Prayer -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">وقت بداية الجلسة الأولى بعد الصلاة (بالدقائق)</label>
                <input 
                  v-model.number="completeForm.first_session_start_minutes_after_prayer" 
                  type="number" 
                  min="0" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
                <p class="text-xs text-gray-500 mt-1">
                  مثال: 0 = مباشرة بعد الصلاة، 15 = بعد 15 دقيقة من الصلاة
                </p>
              </div>

              <!-- Session Count -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">عدد الجلسات</label>
                <input 
                  v-model.number="completeForm.session_count" 
                  type="number" 
                  min="1" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Session Duration -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">مدة الجلسة (بالدقائق)</label>
                <input 
                  v-model.number="completeForm.session_duration_minutes" 
                  type="number" 
                  min="1" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Session Name (Optional) -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">اسم الجلسة (اختياري - سيتم إنشاؤها تلقائياً إذا لم تكن موجودة)</label>
                <input 
                  v-model="completeForm.sessions_name" 
                  type="text" 
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="مثال: جلسة الحفظ"
                />
              </div>

              <!-- Day of Week -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">يوم الأسبوع</label>
                <div class="space-y-2">
                  <label class="flex items-center">
                    <input 
                      type="checkbox"
                      :checked="isAllDaysSelected"
                      @change="handleCompleteAllDaysChange"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span class="mr-2 text-sm text-gray-700">كل الأيام</span>
                  </label>
                  <div class="grid grid-cols-2 gap-2">
                    <label class="flex items-center" v-for="(day, index) in daysOfWeek" :key="index">
                      <input 
                        type="checkbox"
                        :value="index"
                        v-model="completeForm.selected_days"
                        :disabled="isAllDaysSelected"
                        @change="handleDaySelectionChange"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">{{ day }}</span>
                    </label>
                  </div>
                  <p v-if="!isAllDaysSelected && completeForm.selected_days && completeForm.selected_days.length > 0" class="text-xs text-gray-500 mt-1">
                    تم اختيار {{ completeForm.selected_days.length }} يوم/أيام
                  </p>
                </div>
              </div>

              <!-- Is Active -->
              <div>
                <label class="flex items-center">
                  <input 
                    v-model="completeForm.is_active" 
                    type="checkbox" 
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span class="mr-2 text-sm text-gray-700">نشط</span>
                </label>
              </div>

              <!-- Priority -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الأولوية (0 = أعلى أولوية)</label>
                <input 
                  v-model.number="completeForm.priority" 
                  type="number" 
                  min="0" 
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Error Message -->
              <div v-if="errorMessage" class="p-3 bg-red-50 border border-red-200 rounded-md">
                <p class="text-sm text-red-600">{{ errorMessage }}</p>
              </div>

              <!-- Actions -->
              <div class="flex gap-2 pt-4">
                <button 
                  type="submit" 
                  :disabled="submitting || loadingPrayerTime"
                  class="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg"
                >
                  {{ submitting ? 'جاري الحفظ...' : 'إنشاء الجدول' }}
                </button>
                <button 
                  type="button" 
                  @click="closeModal"
                  class="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
                >
                  إلغاء
                </button>
              </div>
            </form>

            <!-- Traditional Form -->
            <form v-else @submit.prevent="submitForm" class="space-y-4">
              <!-- Session Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الجلسة</label>
                <select 
                  v-model="form.sessions_id" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر الجلسة</option>
                  <option v-for="session in teacherSessions" :key="session.id" :value="session.id">
                    {{ session.sessionsName || session.sessions_name || `جلسة ${session.id}` }}
                  </option>
                </select>
                <p v-if="teacherSessions.length === 0" class="text-xs text-red-500 mt-1">
                  ⚠️ لا توجد جلسات متاحة لهذا المعلم. يرجى التأكد من أن المعلم لديه جلسة نشطة.
                </p>
                <p v-else class="text-xs text-gray-500 mt-1">
                  {{ teacherSessions.length }} جلسة متاحة
                </p>
              </div>

              <!-- Session Time Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">وقت الجلسة</label>
                <select 
                  v-model="form.session_time_id" 
                  required
                  @change="updateSessionTimeInfo"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر وقت الجلسة</option>
                  <option v-for="time in sessionTimes" :key="time.id" :value="time.id">
                    {{ getPrayerNameArabic(time.prayerName) }} - {{ formatTime(time.sessionStartTime) }} - {{ formatTime(time.sessionEndTime) }}
                  </option>
                </select>
                <p v-if="sessionTimes.length === 0" class="text-xs text-red-500 mt-1">
                  ⚠️ لا توجد أوقات جلسات متاحة. يرجى التأكد من وجود أوقات جلسات في المنطقة.
                </p>
                <p v-else class="text-xs text-gray-500 mt-1">
                  {{ sessionTimes.length }} وقت جلسة متاح
                </p>
              </div>

              <!-- Day of Week -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">يوم الأسبوع</label>
                <div class="space-y-2">
                  <label class="flex items-center">
                    <input 
                      type="checkbox"
                      :checked="form.selected_days === null || form.selected_days.includes('all')"
                      @change="handleAllDaysChange"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span class="mr-2 text-sm text-gray-700">كل الأيام</span>
                  </label>
                  <div class="grid grid-cols-2 gap-2">
                    <label class="flex items-center">
                      <input 
                        type="checkbox"
                        :value="0"
                        v-model="form.selected_days"
                        :disabled="form.selected_days === null || form.selected_days.includes('all')"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">الأحد</span>
                    </label>
                    <label class="flex items-center">
                      <input 
                        type="checkbox"
                        :value="1"
                        v-model="form.selected_days"
                        :disabled="form.selected_days === null || form.selected_days.includes('all')"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">الإثنين</span>
                    </label>
                    <label class="flex items-center">
                      <input 
                        type="checkbox"
                        :value="2"
                        v-model="form.selected_days"
                        :disabled="form.selected_days === null || form.selected_days.includes('all')"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">الثلاثاء</span>
                    </label>
                    <label class="flex items-center">
                      <input 
                        type="checkbox"
                        :value="3"
                        v-model="form.selected_days"
                        :disabled="form.selected_days === null || form.selected_days.includes('all')"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">الأربعاء</span>
                    </label>
                    <label class="flex items-center">
                      <input 
                        type="checkbox"
                        :value="4"
                        v-model="form.selected_days"
                        :disabled="form.selected_days === null || form.selected_days.includes('all')"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">الخميس</span>
                    </label>
                    <label class="flex items-center">
                      <input 
                        type="checkbox"
                        :value="5"
                        v-model="form.selected_days"
                        :disabled="form.selected_days === null || form.selected_days.includes('all')"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">الجمعة</span>
                    </label>
                    <label class="flex items-center">
                      <input 
                        type="checkbox"
                        :value="6"
                        v-model="form.selected_days"
                        :disabled="form.selected_days === null || form.selected_days.includes('all')"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">السبت</span>
                    </label>
                  </div>
                  <p v-if="form.selected_days && form.selected_days.length > 0 && !form.selected_days.includes('all')" class="text-xs text-gray-500 mt-1">
                    تم اختيار {{ form.selected_days.length }} يوم/أيام
                  </p>
                </div>
              </div>

              <!-- Session Count -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">عدد الجلسات</label>
                <input 
                  v-model.number="form.session_count" 
                  type="number" 
                  min="1" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Session Duration -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">مدة الجلسة (بالدقائق)</label>
                <input 
                  v-model.number="form.session_duration_minutes" 
                  type="number" 
                  min="1" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Total Duration Info -->
              <div v-if="form.session_count && form.session_duration_minutes && selectedSessionTime" 
                   class="p-3 bg-blue-50 rounded-md">
                <p class="text-sm text-gray-700">
                  <span class="font-medium">المدة الإجمالية:</span> 
                  {{ form.session_count * form.session_duration_minutes }} دقيقة
                </p>
                <p class="text-sm text-gray-700 mt-1">
                  <span class="font-medium">النافذة الزمنية المتاحة:</span> 
                  {{ availableWindowMinutes }} دقيقة
                </p>
                <p v-if="(form.session_count * form.session_duration_minutes) > availableWindowMinutes" 
                   class="text-sm text-red-600 mt-1 font-medium">
                  ⚠️ المدة الإجمالية تتجاوز النافذة الزمنية المتاحة!
                </p>
              </div>

              <!-- Is Active -->
              <div>
                <label class="flex items-center">
                  <input 
                    v-model="form.is_active" 
                    type="checkbox" 
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span class="mr-2 text-sm text-gray-700">نشط</span>
                </label>
              </div>

              <!-- Priority -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الأولوية (0 = أعلى أولوية)</label>
                <input 
                  v-model.number="form.priority" 
                  type="number" 
                  min="0" 
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Error Message -->
              <div v-if="errorMessage" class="p-3 bg-red-50 border border-red-200 rounded-md">
                <p class="text-sm text-red-600">{{ errorMessage }}</p>
              </div>

              <!-- Actions -->
              <div class="flex gap-2 pt-4">
                <button 
                  type="submit" 
                  :disabled="submitting"
                  class="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg"
                >
                  {{ submitting ? 'جاري الحفظ...' : 'حفظ' }}
                </button>
                <button 
                  type="button" 
                  @click="closeModal"
                  class="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import url from '@/url';
import * as teacherScheduleService from '@/services/teacherSchedule';

export default defineComponent({
  name: 'TeacherScheduleManagement',
  data() {
    return {
      teachers: [],
      selectedTeacherId: '',
      schedules: [],
      schedulesByDay: null,
      schedulesByGroup: null,
      loading: false,
      showModal: false,
      editingSchedule: null,
      submitting: false,
      errorMessage: '',
      teacherSessions: [],
      sessionTimes: [],
      selectedSessionTime: null,
      viewMode: 'table', // 'table' or 'grouped'
      filterActive: 'all', // 'all', 'true', 'false'
      useCompleteForm: true, // Toggle between complete and traditional form
      regions: [],
      loadingPrayerTime: false,
      daysOfWeek: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
      form: {
        sessions_id: '',
        session_time_id: '',
        day_of_week: null, // For single day (backward compatible)
        selected_days: [], // For multiple days selection
        session_count: 1,
        session_duration_minutes: 30,
        is_active: true,
        priority: 0,
      },
      completeForm: {
        teacher_id: '',
        region_id: '',
        prayer_name: '',
        prayer_time: '',
        first_session_start_minutes_after_prayer: 0,
        session_count: 1,
        session_duration_minutes: 30,
        sessions_name: '',
        selected_days: [],
        is_active: true,
        priority: 0,
      },
    };
  },
  computed: {
    availableWindowMinutes() {
      if (!this.selectedSessionTime) return 0;
      const start = this.parseTime(this.selectedSessionTime.sessionStartTime);
      const end = this.parseTime(this.selectedSessionTime.sessionEndTime);
      if (!start || !end) return 0;
      return Math.round((end - start) / (1000 * 60));
    },
    filteredSchedules() {
      if (!this.schedules || this.schedules.length === 0) return [];
      if (this.filterActive === 'all') return this.schedules;
      const isActive = this.filterActive === 'true';
      return this.schedules.filter(s => s.isActive === isActive);
    },
    orderedSchedulesByDay() {
      if (!this.schedulesByDay) return {};
      
      // Define the order: all_days first, then days of week
      const dayOrder = ['all_days', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const ordered = {};
      
      // First, add all_days if it exists and has items
      if (this.schedulesByDay.all_days && this.schedulesByDay.all_days.length > 0) {
        ordered.all_days = this.schedulesByDay.all_days;
      }
      
      // Then add other days in order
      dayOrder.forEach(dayKey => {
        if (dayKey !== 'all_days' && this.schedulesByDay[dayKey] && this.schedulesByDay[dayKey].length > 0) {
          ordered[dayKey] = this.schedulesByDay[dayKey];
        }
      });
      
      return ordered;
    },
    orderedSchedulesByGroup() {
      if (!this.schedulesByGroup) return {};
      
      // Sort groups by scheduleGroupId (ascending)
      const sortedGroupIds = Object.keys(this.schedulesByGroup).sort((a, b) => {
        const idA = parseInt(a) || 0;
        const idB = parseInt(b) || 0;
        return idA - idB;
      });
      
      const ordered = {};
      sortedGroupIds.forEach(groupId => {
        if (this.schedulesByGroup[groupId] && this.schedulesByGroup[groupId].length > 0) {
          ordered[groupId] = this.schedulesByGroup[groupId];
        }
      });
      
      return ordered;
    },
  },
  async mounted() {
    await this.loadTeachers();
  },
  methods: {
    async loadTeachers() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.get_all_teachers,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
            params: { enabled: true },
          }
        );
        if (response.data?.result?.data) {
          this.teachers = response.data.result.data;
        }
      } catch (error) {
        console.error('Error loading teachers:', error);
        alert('فشل في تحميل قائمة المعلمين');
      }
    },
    async loadRegions() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.master_regions,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );
        if (response.data?.result?.data) {
          this.regions = response.data.result.data;
        }
      } catch (error) {
        console.error('Error loading regions:', error);
        this.regions = [];
      }
    },
    async loadSchedules() {
      if (!this.selectedTeacherId) {
        this.schedules = [];
        this.schedulesByDay = null;
        this.schedulesByGroup = null;
        return;
      }
      
      this.loading = true;
      try {
        // Get all schedules (not grouped by day, we'll group by scheduleGroupId)
        const result = await teacherScheduleService.getTeacherSchedules(this.selectedTeacherId);
        this.schedules = result.data || [];
        
        // Group schedules by scheduleGroupId (this is the main grouping method)
        this.schedulesByGroup = this.groupSchedulesByGroupId(this.schedules);
        
        // Keep schedulesByDay for backward compatibility (if needed)
        this.schedulesByDay = null;
        
        await this.loadTeacherSessions();
      } catch (error) {
        console.error('Error loading schedules:', error);
        alert('فشل في تحميل الجداول');
      } finally {
        this.loading = false;
      }
    },
    groupSchedulesByGroupId(schedules) {
      const grouped = {};
      
      schedules.forEach(schedule => {
        // Use scheduleGroupId (from backend) or schedule_group_id (alternative field name)
        // If not available, use schedule id as fallback for backward compatibility
        const groupId = schedule.scheduleGroupId || schedule.schedule_group_id || schedule.id || 'no-group';
        
        // Convert to string for consistent key usage
        const groupKey = String(groupId);
        
        if (!grouped[groupKey]) {
          grouped[groupKey] = [];
        }
        
        grouped[groupKey].push(schedule);
      });
      
      return grouped;
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'grouped' : 'table';
    },
    applyFilter() {
      // Filter is applied via computed property
      // This method is here for potential future enhancements
    },
    getDayLabel(dayKey) {
      const labels = {
        'all_days': 'كل الأيام',
        'sunday': 'الأحد',
        'monday': 'الإثنين',
        'tuesday': 'الثلاثاء',
        'wednesday': 'الأربعاء',
        'thursday': 'الخميس',
        'friday': 'الجمعة',
        'saturday': 'السبت',
      };
      return labels[dayKey] || dayKey;
    },
    async loadTeacherSessions() {
      if (!this.selectedTeacherId) {
        this.teacherSessions = [];
        return;
      }
      
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.master_sessions,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );
        
        console.log('Sessions API Response:', response.data);
        
        if (response.data?.result?.data) {
          const allSessions = response.data.result.data;
          console.log('All sessions from API:', allSessions);
          console.log('Selected teacher ID:', this.selectedTeacherId);
          
          // Try different field names for teacher ID
          this.teacherSessions = allSessions.filter(session => {
            // Try multiple possible field names
            const teacherId = session.userId || session.user_id || session.userId;
            const matches = teacherId === parseInt(this.selectedTeacherId) || 
                           teacherId == this.selectedTeacherId ||
                           String(teacherId) === String(this.selectedTeacherId);
            
            console.log('Session filter check:', {
              sessionId: session.id,
              sessionName: session.sessionsName,
              sessionUserId: teacherId,
              selectedTeacherId: this.selectedTeacherId,
              matches
            });
            
            return matches;
          });
          
          console.log('Filtered teacher sessions (by ID):', this.teacherSessions);
          
          // If no sessions found by ID, try alternative approach by name
          if (this.teacherSessions.length === 0) {
            const teacher = this.teachers.find(t => t.id === parseInt(this.selectedTeacherId));
            if (teacher && teacher.fullName) {
              this.teacherSessions = allSessions.filter(session => {
                const matches = session.sessionsName && 
                               (session.sessionsName.includes(teacher.fullName) ||
                                session.sessionsName === `جلسة ${teacher.fullName}` ||
                                session.sessionsName === `Session ${teacher.fullName}`);
                if (matches) {
                  console.log('Found session by name:', session);
                }
                return matches;
              });
              console.log('Found sessions by name:', this.teacherSessions);
            }
          }
          
          // If still no sessions, show all sessions for debugging
          if (this.teacherSessions.length === 0) {
            console.warn('No sessions found for teacher. All available sessions:', allSessions);
            console.warn('Available teacher IDs in sessions:', allSessions.map(s => ({
              id: s.id,
              name: s.sessionsName,
              userId: s.userId || s.user_id
            })));
          }
        } else {
          console.warn('No sessions data in response:', response.data);
          this.teacherSessions = [];
        }
      } catch (error) {
        console.error('Error loading teacher sessions:', error);
        this.teacherSessions = [];
      }
    },
    async loadSessionTimes() {
      if (!this.selectedTeacherId) {
        this.sessionTimes = [];
        return;
      }
      
      const teacher = this.teachers.find(t => t.id === parseInt(this.selectedTeacherId));
      if (!teacher) {
        console.warn('Teacher not found for ID:', this.selectedTeacherId);
        this.sessionTimes = [];
        return;
      }

      // Try to get regionId from teacher, or try to get it from teacher's session
      let regionId = teacher.regionId;
      
      // If regionId is not available, try to get it from teacher's session
      if (!regionId && this.teacherSessions.length > 0) {
        // Try to get region from first session
        const firstSession = this.teacherSessions[0];
        if (firstSession.regionId) {
          regionId = firstSession.regionId;
        }
      }

      if (!regionId) {
        console.warn('No regionId found for teacher:', teacher);
        // Try to load from all regions if teacher region is not available
        await this.loadSessionTimesFromAllRegions();
        return;
      }
      
      try {
        console.log('Loading session times for region:', regionId);
        const sessionTimesResponse = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.get_session_times + regionId,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );
        
        console.log('Session Times API Response:', sessionTimesResponse.data);
        
        if (sessionTimesResponse.data?.result?.data) {
          this.sessionTimes = sessionTimesResponse.data.result.data;
          console.log('Loaded session times:', this.sessionTimes.length);
        } else {
          console.warn('No session times data in response');
          this.sessionTimes = [];
        }
      } catch (error) {
        console.error('Error loading session times:', error);
        this.sessionTimes = [];
        // Try to load from all regions as fallback
        await this.loadSessionTimesFromAllRegions();
      }
    },
    async loadSessionTimesFromAllRegions() {
      // Fallback: Try to load session times from all regions
      try {
        console.log('Attempting to load session times from all regions...');
        // Get all regions first
        const regionsResponse = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.master_regions,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );
        
        if (regionsResponse.data?.result?.data && regionsResponse.data.result.data.length > 0) {
          const regions = regionsResponse.data.result.data;
          const allSessionTimes = [];
          
          // Load session times from all regions
          for (const region of regions) {
            try {
              const sessionTimesResponse = await axios.get(
                process.env.VUE_APP_SERVICE_URL + url.get_session_times + region.id,
                {
                  headers: { 'X-Authorization': localStorage.getItem('token') },
                }
              );
              
              if (sessionTimesResponse.data?.result?.data) {
                allSessionTimes.push(...sessionTimesResponse.data.result.data);
              }
            } catch (err) {
              console.warn(`Failed to load session times for region ${region.id}:`, err);
            }
          }
          
          if (allSessionTimes.length > 0) {
            // Remove duplicates based on id
            this.sessionTimes = allSessionTimes.filter((time, index, self) =>
              index === self.findIndex(t => t.id === time.id)
            );
            console.log('Loaded session times from all regions:', this.sessionTimes.length);
          } else {
            this.sessionTimes = [];
          }
        }
      } catch (error) {
        console.error('Error loading session times from all regions:', error);
        this.sessionTimes = [];
      }
    },
    async openCreateModal() {
      this.editingSchedule = null;
      this.useCompleteForm = true;
      this.form = {
        sessions_id: '',
        session_time_id: '',
        day_of_week: null,
        selected_days: [],
        session_count: 1,
        session_duration_minutes: 30,
        is_active: true,
        priority: 0,
      };
      this.completeForm = {
        teacher_id: this.selectedTeacherId || '',
        region_id: '',
        prayer_name: '',
        prayer_time: '',
        first_session_start_minutes_after_prayer: 0,
        session_count: 1,
        session_duration_minutes: 30,
        sessions_name: '',
        selected_days: [],
        is_active: true,
        priority: 0,
      };
      this.errorMessage = '';
      this.selectedSessionTime = null;
      await Promise.all([
        this.loadRegions(),
        this.loadTeacherSessions(),
        this.loadSessionTimes()
      ]);
      this.showModal = true;
    },
    openEditModal(schedule) {
      this.editingSchedule = schedule;
      this.form = {
        sessions_id: schedule.sessionId,
        session_time_id: schedule.sessionTimeId,
        day_of_week: schedule.dayOfWeek,
        selected_days: schedule.dayOfWeek === null ? ['all'] : [schedule.dayOfWeek],
        session_count: schedule.sessionCount,
        session_duration_minutes: schedule.sessionDurationMinutes,
        is_active: schedule.isActive,
        priority: schedule.priority,
      };
      this.errorMessage = '';
      this.loadSessionTimes();
      this.updateSessionTimeInfo();
      this.showModal = true;
    },
    handleAllDaysChange(event) {
      if (event.target.checked) {
        this.form.selected_days = ['all'];
        this.form.day_of_week = null;
      } else {
        this.form.selected_days = [];
        this.form.day_of_week = null;
      }
    },
    updateSessionTimeInfo() {
      if (this.form.session_time_id) {
        this.selectedSessionTime = this.sessionTimes.find(
          st => st.id === parseInt(this.form.session_time_id)
        );
      } else {
        this.selectedSessionTime = null;
      }
    },
    async submitForm() {
      // Validate form before submission
      if (!this.form.sessions_id || !this.form.session_time_id) {
        this.errorMessage = 'يرجى اختيار الجلسة ووقت الجلسة';
        return;
      }

      if (!this.form.selected_days || this.form.selected_days.length === 0) {
        this.errorMessage = 'يرجى اختيار يوم واحد على الأقل';
        return;
      }

      if (!this.form.session_count || this.form.session_count < 1) {
        this.errorMessage = 'عدد الجلسات يجب أن يكون أكبر من صفر';
        return;
      }

      if (!this.form.session_duration_minutes || this.form.session_duration_minutes < 1) {
        this.errorMessage = 'مدة الجلسة يجب أن تكون أكبر من صفر';
        return;
      }

      // Check if total duration exceeds window
      if (this.selectedSessionTime) {
        const totalDuration = this.form.session_count * this.form.session_duration_minutes;
        if (totalDuration > this.availableWindowMinutes) {
          this.errorMessage = `المدة الإجمالية (${totalDuration} دقيقة) تتجاوز النافذة الزمنية المتاحة (${this.availableWindowMinutes} دقيقة)`;
          return;
        }
      }

      this.submitting = true;
      this.errorMessage = '';
      
      try {
        if (this.editingSchedule) {
          // For editing, use single day (backward compatible)
          const formData = {
            ...this.form,
            day_of_week: this.form.selected_days.includes('all') ? null : (this.form.selected_days[0] ?? null),
          };
          delete formData.selected_days;
          
          await teacherScheduleService.updateTeacherSchedule(
            this.selectedTeacherId,
            this.editingSchedule.id,
            formData
          );
          alert('تم تحديث الجدول بنجاح');
        } else {
          // For creating, handle multiple days
          const daysToCreate = this.form.selected_days.includes('all') 
            ? [null] // null = all days
            : this.form.selected_days.filter(d => d !== 'all').map(d => parseInt(d));
          
          let createdCount = 0;
          let failedCount = 0;
          const errors = [];
          
          for (const dayOfWeek of daysToCreate) {
            try {
              const formData = {
                sessions_id: this.form.sessions_id,
                session_time_id: this.form.session_time_id,
                day_of_week: dayOfWeek,
                session_count: this.form.session_count,
                session_duration_minutes: this.form.session_duration_minutes,
                is_active: this.form.is_active,
                priority: this.form.priority,
              };
              
              await teacherScheduleService.createTeacherSchedule(
                this.selectedTeacherId,
                formData
              );
              createdCount++;
            } catch (error) {
              failedCount++;
              const dayName = dayOfWeek === null ? 'كل الأيام' : this.getDayName(dayOfWeek);
              errors.push(`${dayName}: ${error.message || 'فشل في الإنشاء'}`);
            }
          }
          
          if (createdCount > 0) {
            if (failedCount === 0) {
              alert(`تم إنشاء ${createdCount} جدول بنجاح`);
            } else {
              alert(`تم إنشاء ${createdCount} جدول بنجاح، وفشل ${failedCount} جدول:\n${errors.join('\n')}`);
            }
          } else {
            this.errorMessage = `فشل في إنشاء الجداول:\n${errors.join('\n')}`;
            return;
          }
        }
        this.closeModal();
        await this.loadSchedules();
      } catch (error) {
        this.errorMessage = error.message || 'فشل في حفظ الجدول';
      } finally {
        this.submitting = false;
      }
    },
    getDayName(dayOfWeek) {
      const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      return days[dayOfWeek] || '';
    },
    async deleteSchedule(schedule) {
      if (!confirm(`هل أنت متأكد من حذف هذا الجدول؟`)) return;
      
      try {
        await teacherScheduleService.deleteTeacherSchedule(
          this.selectedTeacherId,
          schedule.id
        );
        alert('تم حذف الجدول بنجاح');
        await this.loadSchedules();
      } catch (error) {
        alert(error.message || 'فشل في حذف الجدول');
      }
    },
    onTeacherChange() {
      // If teacher is selected, optionally pre-fill region from teacher's data
      if (this.completeForm.teacher_id) {
        const teacher = this.teachers.find(t => t.id === parseInt(this.completeForm.teacher_id));
        if (teacher && teacher.regionId && !this.completeForm.region_id) {
          this.completeForm.region_id = teacher.regionId;
        }
      }
    },
    async onRegionChange() {
      this.completeForm.prayer_name = '';
      this.completeForm.prayer_time = '';
      this.loadingPrayerTime = false;
    },
    async onPrayerChange() {
      if (!this.completeForm.region_id || !this.completeForm.prayer_name) {
        this.completeForm.prayer_time = '';
        return;
      }

      this.loadingPrayerTime = true;
      this.completeForm.prayer_time = '';

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}${url.get_prayer_time_from_aladhan}${this.completeForm.region_id}/prayer-time/${this.completeForm.prayer_name}`,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );

        if (response.data?.result?.status === 200 && response.data?.result?.data?.prayerTime) {
          this.completeForm.prayer_time = response.data.result.data.prayerTime;
        } else {
          this.errorMessage = response.data?.result?.mesAr || 'فشل في جلب وقت الصلاة';
        }
      } catch (error) {
        console.error('Error fetching prayer time:', error);
        this.errorMessage = error.response?.data?.result?.mesAr || 'فشل في جلب وقت الصلاة';
      } finally {
        this.loadingPrayerTime = false;
      }
    },
    handleCompleteAllDaysChange(event) {
      if (event.target.checked) {
        // Select all days = empty array (means all days)
        this.completeForm.selected_days = [];
      } else {
        // If unchecking "all days", don't do anything - let user select specific days
        // Or you could set to first day as default
      }
    },
    handleDaySelectionChange() {
      // When a specific day is selected, ensure "all days" is unchecked
      // This is handled by the computed property isAllDaysSelected
    },
    async submitCompleteForm() {
      // Validate form
      if (!this.completeForm.teacher_id) {
        this.errorMessage = 'يرجى اختيار المعلم';
        return;
      }

      if (!this.completeForm.region_id) {
        this.errorMessage = 'يرجى اختيار المنطقة';
        return;
      }

      if (!this.completeForm.prayer_name) {
        this.errorMessage = 'يرجى اختيار الصلاة';
        return;
      }

      if (this.completeForm.first_session_start_minutes_after_prayer < 0) {
        this.errorMessage = 'وقت بداية الجلسة الأولى يجب أن يكون 0 أو أكثر';
        return;
      }

      if (!this.completeForm.session_count || this.completeForm.session_count < 1) {
        this.errorMessage = 'عدد الجلسات يجب أن يكون أكبر من صفر';
        return;
      }

      if (!this.completeForm.session_duration_minutes || this.completeForm.session_duration_minutes < 1) {
        this.errorMessage = 'مدة الجلسة يجب أن تكون أكبر من صفر';
        return;
      }

      this.submitting = true;
      this.errorMessage = '';

      try {
        const formData = {
          region_id: this.completeForm.region_id,
          prayer_name: this.completeForm.prayer_name,
          session_count: this.completeForm.session_count,
          session_duration_minutes: this.completeForm.session_duration_minutes,
          first_session_start_minutes_after_prayer: this.completeForm.first_session_start_minutes_after_prayer,
          is_active: this.completeForm.is_active,
          priority: this.completeForm.priority,
        };

        // Add sessions_name if provided
        if (this.completeForm.sessions_name) {
          formData.sessions_name = this.completeForm.sessions_name;
        }

        // Handle selected days
        if (this.completeForm.selected_days && this.completeForm.selected_days.length > 0) {
          formData.selected_days = this.completeForm.selected_days;
        } else {
          formData.selected_days = []; // Empty array = all days
        }

        const result = await teacherScheduleService.createTeacherScheduleComplete(
          this.completeForm.teacher_id,
          formData
        );

        if (result && result.created > 0) {
          alert(`تم إنشاء ${result.created} جدول بنجاح`);
          this.closeModal();
          // Update selected teacher if not already selected
          if (!this.selectedTeacherId || this.selectedTeacherId !== this.completeForm.teacher_id) {
            this.selectedTeacherId = this.completeForm.teacher_id;
            await this.loadSchedules();
          } else {
            await this.loadSchedules();
          }
        } else {
          this.errorMessage = 'فشل في إنشاء الجدول';
        }
      } catch (error) {
        console.error('Error creating complete schedule:', error);
        this.errorMessage = error.message || 'فشل في إنشاء الجدول';
      } finally {
        this.submitting = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.editingSchedule = null;
      this.errorMessage = '';
      this.selectedSessionTime = null;
      this.loadingPrayerTime = false;
    },
    formatTime(time) {
      if (!time) return 'N/A';
      
      let hours = 0;
      let minutes = 0;
      
      if (typeof time === 'string') {
        const parts = time.split(':');
        if (parts.length >= 2) {
          hours = parseInt(parts[0], 10);
          minutes = parseInt(parts[1], 10);
        } else {
          return time;
        }
      } else if (typeof time === 'number') {
        // If it's a timestamp or number
        const date = new Date(time);
        hours = date.getHours();
        minutes = date.getMinutes();
      } else {
        return time;
      }
      
      // Convert to 12-hour format
      const period = hours >= 12 ? 'م' : 'ص';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      
      return `${displayHours}:${displayMinutes} ${period}`;
    },
    parseTime(timeStr) {
      if (!timeStr) return null;
      const parts = timeStr.split(':');
      if (parts.length >= 2) {
        const date = new Date();
        date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0);
        return date;
      }
      return null;
    },
    getPrayerNameArabic(prayerName) {
      if (!prayerName) return 'N/A';
      
      // Handle both English and Arabic formats
      const prayerNames = {
        // English formats (from API - uppercase)
        'FAJR': 'الفجر',
        'DHUHR': 'الظهر',
        'ASR': 'العصر',
        'MAGHRIB': 'المغرب',
        'ISHA': 'العشاء',
        // Lowercase formats
        'Fajr': 'الفجر',
        'Dhuhr': 'الظهر',
        'Asr': 'العصر',
        'Maghrib': 'المغرب',
        'Isha': 'العشاء',
        // Already in Arabic (if backend returns Arabic)
        'الفجر': 'الفجر',
        'الظهر': 'الظهر',
        'العصر': 'العصر',
        'المغرب': 'المغرب',
        'العشاء': 'العشاء',
      };
      
      // Try exact match first
      if (prayerNames[prayerName]) {
        return prayerNames[prayerName];
      }
      
      // Try case-insensitive match
      const upperCase = prayerName.toUpperCase();
      if (prayerNames[upperCase]) {
        return prayerNames[upperCase];
      }
      
      // Return original if no match found
      return prayerName;
    },
  },
});
</script>

<style scoped>
/* Add any custom styles here */
</style>

